import React from 'react';
import { Form, Input, Button, Row, Col, FormInstance } from 'antd';
import { EyeInvisibleOutlined, EyeOutlined } from '@ant-design/icons';
import { ValidationErrors } from 'constants/validations';

const { Item } = Form;

export interface ResetPasswordFormData {
  password: string;
  passwordRepeat: string;
}

export interface ResetPasswordFormProps {
  form: FormInstance;
  onFinish: (values: ResetPasswordFormData) => void;
  loading?: boolean;
  submitButtonText?: string;
  onBackToLogin?: () => void;
  backToLoginText?: string;
  showBackToLogin?: boolean;
  minPasswordLength?: number;
  customPasswordValidation?: (value: string) => Promise<void>;
}

export const ResetPasswordForm: React.FC<ResetPasswordFormProps> = ({
  form,
  onFinish,
  loading = false,
  submitButtonText = 'Reset Password',
  onBackToLogin,
  backToLoginText = 'Back to Sign In',
  showBackToLogin = true,
  minPasswordLength = 6,
  customPasswordValidation,
}) => {
  return (
    <Form form={form} layout="vertical" onFinish={onFinish}>
      <Row gutter={[0, 24]}>
        <Col span={24}>
          <Item
            label="New Password *"
            name="password"
            rules={[
              {
                required: true,
                message: ValidationErrors.required('New Password'),
              },
              {
                validator: async (_, value) => {
                  if (customPasswordValidation) {
                    return customPasswordValidation(value);
                  } else if (value && value.trim().length < minPasswordLength) {
                    return Promise.reject(new Error(`Password must be at least ${minPasswordLength} characters long`));
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <Input.Password
              iconRender={(visible) => (visible ? <EyeOutlined /> : <EyeInvisibleOutlined />)}
              size="large"
            />
          </Item>
        </Col>
        <Col span={24}>
          <Item
            label="Confirm New Password *"
            name="passwordRepeat"
            dependencies={['password']}
            rules={[
              {
                required: true,
                message: ValidationErrors.required('Confirm New Password'),
              },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('password') === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error('Passwords do not match!'));
                },
              }),
              {
                validator: async (_, value) => {
                  if (customPasswordValidation) {
                    return customPasswordValidation(value);
                  } else if (value && value.trim().length < minPasswordLength) {
                    return Promise.reject(new Error(`Password must be at least ${minPasswordLength} characters long`));
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <Input.Password
              iconRender={(visible) => (visible ? <EyeOutlined /> : <EyeInvisibleOutlined />)}
              size="large"
            />
          </Item>
        </Col>
        <Col span={24}>
          <Button
            size="large"
            type="primary"
            htmlType="submit"
            block
            loading={loading}
            style={{
              backgroundColor: '#05796B',
              borderColor: '#05796B',
              height: '48px',
              fontSize: '16px',
              fontWeight: '600',
            }}
          >
            {submitButtonText}
          </Button>
        </Col>
        {showBackToLogin && onBackToLogin && (
          <Col span={24} style={{ textAlign: 'center', marginTop: '16px' }}>
            <span
              onClick={onBackToLogin}
              style={{
                color: '#05796B',
                cursor: 'pointer',
                fontSize: '14px',
              }}
            >
              {backToLoginText}
            </span>
          </Col>
        )}
      </Row>
    </Form>
  );
};
